import { useContext } from "react";
import type { ValidationError } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { isAxiosError } from "axios";
import { Link, useNavigate } from "react-router-dom";

import { useLogin, useRegister } from "@/api/auth";
import {
  InsideLabelFieldInput,
  InsideLabelFieldPassword,
  SubscribeButton,
} from "@/components/form";
import AuthPageTitle from "@/components/layout/auth-page-title";
import { AuthContext } from "@/contexts/auth-context";
import {
  emailSchema,
  registerDefaultValues,
  registerSchema,
} from "@/schemas/auth";
import { stopPropagation } from "@/utils/form";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { mutate } = useRegister();
  const { mutate: mutateLogin } = useLogin();
  const { login: performLogin } = useContext(AuthContext);

  const { Field, Subscribe, handleSubmit } = useForm({
    defaultValues: registerDefaultValues,
    validators: {
      onSubmit: ({ value }) => {
        const { RPPS, lastName, firstName, password, email } = value;
        const returnValue: { fields: { [k: string]: ValidationError } } = {
          fields: {},
        };
        if (!RPPS) {
          const RPPSError: ValidationError = "Le numéro RPPS est obligatoire";
          returnValue.fields["RPPS"] = RPPSError;
        }
        if (!lastName) {
          const lastNameError: ValidationError = "Le nom est obligatoire";
          returnValue.fields["lastName"] = lastNameError;
        }
        if (!firstName) {
          const firstNameError: ValidationError = "Le prénom est obligatoire";
          returnValue.fields["firstName"] = firstNameError;
        }
        if (
          !password.match(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>-_])/
          )
        ) {
          const passwordRegexError: ValidationError =
            'Votre mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial (!@#$%^&*(),.?"{}:|<>)';
          returnValue.fields["password"] = passwordRegexError;
        }
        if (password.length < 8) {
          const passwordLengthError: ValidationError =
            "Votre mot de passe doit contenir au moins 8 caractères";
          returnValue.fields["password"] = passwordLengthError;
        }
        if (!emailSchema.safeParse(email).success) {
          const emailError: ValidationError = "Veuillez entrer un email valide";
          returnValue.fields["email"] = emailError;
        }
        return returnValue;
      },
    },
    onSubmit: async ({ value, formApi }) => {
      mutate(value, {
        onSuccess: () => {
          mutateLogin(
            { email: value.email, password: value.password },
            {
              onSuccess: (data) => {
                performLogin(data);
                navigate("/subscription");
              },
            }
          );
        },
        onError: (e) => {
          if (isAxiosError(e)) {
            if (e.response?.data.message === "EMAIL_ALREADY_USED") {
              formApi.setFieldMeta("email", (prev) => ({
                ...prev,
                errors: ["Un utilisateur avec cette adresse mail existe déjà."],
                errorMap: {
                  onChange:
                    "Un utilisateur avec cette adresse mail existe déjà.",
                },
              }));
              return;
            }
          }
        },
      });
    },
  });

  return (
    <form
      onSubmit={(e) => stopPropagation(e, handleSubmit)}
      className="grid grid-flow-row grid-cols-1 gap-12 pr-[112px] pl-[112px] w-full"
    >
      <AuthPageTitle
        title="Inscrivez-vous"
        subTitle="Entrez vos informations pour accéder à votre espace."
      />
      <div className="flex flex-col gap-4">
        <InsideLabelFieldInput
          Field={Field}
          name="lastName"
          label="Nom"
          schema={registerSchema}
        />
        <InsideLabelFieldInput
          Field={Field}
          name="firstName"
          label="Prénom"
          schema={registerSchema}
        />
        <InsideLabelFieldInput
          Field={Field}
          type="number"
          name="RPPS"
          label="N°RPPS"
          schema={registerSchema}
          validators={{ onChange: registerSchema.shape.RPPS }}
        />
        <InsideLabelFieldInput
          Field={Field}
          name="email"
          label="Email"
          schema={registerSchema}
        />
        <InsideLabelFieldPassword
          Field={Field}
          name="password"
          label="Mot de passe"
          schema={registerSchema}
        />
      </div>
      <div className="flex flex-col gap-8">
        <SubscribeButton Subscribe={Subscribe}>S'inscrire</SubscribeButton>

        <div className="flex items-center justify-center w-full gap-2">
          <p className="text-sm">Déjà un compte ?</p>
          <Link to="/connexion">
            <span className="text-sm text-semibold bg-gradient-to-tr from-gradient-from via-gradient-via to-gradient-to bg-clip-text text-transparent">
              Connectez-vous
            </span>
          </Link>
        </div>
      </div>
    </form>
  );
};

export default RegisterPage;
